import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useMaintenanceStatusQuery } from '@shared/api';

/**
 * Плашка «ОСНОВА обновится через 5 минут».
 *
 * Зачем. Обновление пересоздаёт контейнер бэкенда. Задачи из очереди при этом
 * не теряются (прерванный анализ подхватывается заново сразу после
 * перезапуска), но человек, который только что нажал «анализировать» или
 * заполняет длинную форму, иначе видит лишь то, что приложение перестало
 * отвечать. Предупреждение за несколько минут решает это без всякой техники:
 * люди успевают дописать и сохранить.
 *
 * Три состояния:
 *  - `soon` — обновление объявлено, идёт отсчёт;
 *  - `running` — перезапуск начался (отсчёт дошёл до нуля либо сервер уже не
 *    отвечает, а окно было объявлено) — тогда «нет связи» не пугает человека,
 *    он знает причину;
 *  - `done` — сервер снова отвечает и объявление снято: предлагаем обновить
 *    страницу, чтобы перейти на новую версию. Автоматически не перезагружаем —
 *    в открытых формах может быть несохранённый текст.
 */

const Wrap = styled.div<{ $tone: 'warn' | 'ok' }>`
  grid-area: banner;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(6)};
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  ${({ theme, $tone }) =>
    $tone === 'warn'
      ? css`
          background: ${theme.colors.surfaceHover};
          color: ${theme.colors.text.primary};
          border-bottom: 2px solid ${theme.colors.status.warning};
        `
      : css`
          background: ${theme.colors.surfaceHover};
          color: ${theme.colors.text.primary};
          border-bottom: 2px solid ${theme.colors.status.success};
        `}
`;

const Strong = styled.span`
  font-weight: 600;
`;

const Hint = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ReloadBtn = styled.button`
  flex: none;
  padding: 3px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

/** «4 мин 32 с», «45 с» — без двоеточий, читается с одного взгляда. */
function formatLeft(seconds: number): string {
  if (seconds < 60) return `${seconds} с`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec === 0 ? `${min} мин` : `${min} мин ${sec} с`;
}

export function MaintenanceBanner() {
  // Пока окно объявлено — спрашиваем часто (надо вовремя поймать и начало, и
  // конец работ), в обычное время раз в полминуты.
  const [fast, setFast] = useState(false);
  const { data, isError, isSuccess } = useMaintenanceStatusQuery(undefined, {
    pollingInterval: fast ? 5000 : 30000,
    // Опрос должен идти и когда вкладка в фоне: иначе человек вернётся к
    // открытой странице уже посреди перезапуска, без предупреждения.
    skipPollingIfUnfocused: false,
  });

  // Отсчёт ведём от серверных секунд: часы на клиенте бывают сбиты, и
  // «обновление через −7 минут» выглядело бы поломкой.
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!data?.active) {
      setLeft(null);
      return;
    }
    setLeft(data.seconds_until ?? 0);
  }, [data?.active, data?.seconds_until]);

  useEffect(() => {
    if (!data?.active) return;
    const t = setInterval(() => setLeft((v) => (v === null ? null : Math.max(0, v - 1))), 1000);
    return () => clearInterval(t);
  }, [data?.active]);

  // До чего дело дошло в этой сессии. Нужно, чтобы отличить «сервер молчит,
  // потому что обновляется» от обычного обрыва связи, и чтобы после работ
  // предложить перезагрузку — но не предлагать её, если объявление просто
  // отменили, не начав перезапуск.
  const stage = useRef<'idle' | 'soon' | 'running'>('idle');

  // Сервер не ответил — значит перезапуск уже начался, что бы ни говорил
  // отсчёт: деплой мог пойти раньше срока. Мигания сети тут не видно, их
  // гасит baseQuery — он повторяет чтение дважды, так что ошибка означает
  // три неудачные попытки подряд за несколько секунд.
  const silent = isError;

  // На первом рендере с новым ответом локальный отсчёт ещё не заведён
  // (useEffect отработает после), поэтому берём серверное значение — иначе
  // окно на пять минут вперёд мгновенно засчиталось бы как начавшееся.
  const secondsLeft = left ?? data?.seconds_until ?? 0;

  // Движение только вперёд: отсчёт → перезапуск → обновление установлено.
  // Иначе баннер мигал бы между «через 4 минуты» и «идёт обновление» —
  // опрос чередует неудачную попытку с новой, и в промежутке RTK Query
  // отдаёт прежний (уже неверный) ответ сервера.
  let phase: 'none' | 'soon' | 'running' | 'done' = 'none';
  if (stage.current === 'running') {
    // Работы закончились, когда бэкенд снова отвечает и объявление снято.
    // `stage` не сбрасываем — предложение обновить страницу должно держаться
    // до самой перезагрузки.
    phase = isSuccess && !data?.active ? 'done' : 'running';
  } else if (silent && stage.current === 'soon') {
    phase = 'running';
    stage.current = 'running';
  } else if (data?.active) {
    phase = secondsLeft > 0 ? 'soon' : 'running';
    stage.current = phase;
  } else if (isSuccess && stage.current === 'soon') {
    // Объявление сняли, не начав работ — молчим, перезагружаться незачем.
    stage.current = 'idle';
  }

  useEffect(() => {
    setFast(phase !== 'none');
  }, [phase]);

  if (phase === 'none') return null;

  if (phase === 'done') {
    return (
      <Wrap $tone="ok">
        <Strong>Обновление установлено.</Strong>
        <Hint>Обновите страницу, чтобы перейти на новую версию.</Hint>
        <ReloadBtn type="button" onClick={() => window.location.reload()}>
          Обновить страницу
        </ReloadBtn>
      </Wrap>
    );
  }

  if (phase === 'running') {
    return (
      <Wrap $tone="warn">
        <Strong>Идёт обновление ОСНОВЫ.</Strong>
        <Hint>
          Страница снова заработает через минуту — закрывать её не нужно. Начатые анализы
          продолжатся сами.
        </Hint>
      </Wrap>
    );
  }

  return (
    <Wrap $tone="warn">
      <Strong>Внимание: обновление через {formatLeft(secondsLeft)}.</Strong>
      <Hint>
        {data?.message?.trim()
          ? data.message
          : 'Допишите и сохраните начатое — на минуту ОСНОВА станет недоступна. Запущенные анализы не потеряются: они продолжатся после обновления.'}
      </Hint>
    </Wrap>
  );
}
